import { Box, Stack } from "@mui/material";
import {
  useUsersListQuery,
  type UsersListParams,
  mapUsersFiltersToParams,
  mapUsersSortColumn,
} from "Api/Queries/User/useUsersListQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { CheckboxInput } from "Components/Shared/Inputs/CheckboxInput";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { UserListTable } from "Components/Users/UserListTable/UserListTable";
import { createLicensePaymentReferenceFromUserID } from "Components/Users/utils";
import { useServerTable } from "Hooks/useServerTable";
import type { UserDto } from "Infrastructure/Api/Api";
import { type ReactNode, useMemo, useState } from "react";
import { Resources, useResource } from "Translations/Resources";

type UserWithLicensePaymentReference = UserDto & { paymentReference: string };

export const UserListPage = () => {
  const [isExcludingAnonymous, setIsExcludingAnonymous] = useState(true);
  const { t } = useResource();

  const { queryParams, tableProps, resetPagination } =
    useServerTable<UserWithLicensePaymentReference>({
      defaultSorting: [{ id: "dateCreated", desc: true }],
    });

  const apiParams = useMemo(
    (): UsersListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: mapUsersSortColumn(queryParams.sortBy),
      sortDirection: queryParams.sortDirection,
      excludedAnonymous: isExcludingAnonymous,
      ...mapUsersFiltersToParams(queryParams.filters),
    }),
    [queryParams, isExcludingAnonymous],
  );

  const { data, isLoading, isRefetching, isError } =
    useUsersListQuery(apiParams);

  if (isError) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Pages.Content.Users.Error)}</ErrorText>
      </Layout>
    );
  }

  const dataWithReference = (data?.items || []).map((row) => ({
    ...row,
    paymentReference: createLicensePaymentReferenceFromUserID(row.userID),
  }));

  return (
    <Layout>
      <CheckboxInput
        name="isExcludingAnonymous"
        checked={isExcludingAnonymous}
        label={t(Resources.Pages.Content.Users.ExcludeAnonymous)}
        onChange={(e) => {
          setIsExcludingAnonymous(e.target.checked);
          resetPagination();
        }}
      />
      <Box display="grid" alignItems="start">
        <UserListTable
          isLoading={isLoading}
          isRefetching={isRefetching}
          data={dataWithReference}
          rowCount={data?.totalCount ?? 0}
          tableProps={tableProps}
        />
      </Box>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout title={t(Resources.Pages.Titles.Users)}>
      <StyledPageWrapper maxWidth="unset !important">
        <Stack gap={4}>{children}</Stack>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default UserListPage;
